import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, computed, effect, ElementRef, inject, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgbCollapseModule, NgbModal, NgbModalRef, NgbProgressbar, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ILandingAccessCard, ITargetApp, ITargetAppWithProfiles } from '@core/models';
import {
  AppStore,
  AuthService,
  Console,
  SsoService,
  TargetAppsService,
  ToastService,
  VerificaService,
  WidgetComunicazioniService
} from '@core/services';
import { ApiService } from '@core/services/api/api.service';
import { environment } from '@environments/environment';
import { ContactConfirmationModalComponent } from '@shared/contact-confirmation-modal/contact-confirmation-modal';
import { Footer } from '@shared/footer/footer';
import { Header } from '@shared/header/header';
import { Sidebar } from '@shared/sidebar/sidebar';

@Component({
  selector: 'app-landing-page',
  imports: [CommonModule, Sidebar, Header, Footer, NgbTooltip, NgbProgressbar, NgbCollapseModule],
  templateUrl: './landing-page.html',
  styleUrl: './landing-page.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class LandingPage implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly appStore = inject(AppStore);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly targetAppsService = inject(TargetAppsService);
  private readonly apiService = inject(ApiService);
  private readonly ssoService = inject(SsoService);
  private readonly verificaService = inject(VerificaService);
  private readonly widgetComunicazioniService = inject(WidgetComunicazioniService);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly modalService = inject(NgbModal);
  private readonly document = inject(DOCUMENT);
  private readonly GENERIC_ICON_PATH = '/images/app-icon-generic.svg';
  readonly showWidgetComunicazioni = environment.SHOW_WIDGET_COMUNICAZIONI;
  readonly raggruppaProfili = environment.RAGGRUPPA_PROFILI;

  // Signals reattivi per i dati utente
  readonly user = this.appStore.user;
  readonly isAuthenticated = computed(() => this.appStore.isAuthenticated());

  private readonly shouldLoadTargetApps = signal(false);
  readonly targetAppsResource = this.targetAppsService.createTargetAppsWithProfilesResource(
    () => this.shouldLoadTargetApps()
  );
  readonly isLoadingApps = this.targetAppsResource.isLoading;

  readonly targetAppsWithProfiles = computed(() =>
    this.targetAppsService.applyLandingPortaleVisibility(
      this.targetAppsResource.value(),
      this.user()
    )
  );
  readonly landingAccessCards = computed((): ILandingAccessCard[] => {
    if (this.raggruppaProfili) {
      return [];
    }
    return this.targetAppsService.toLandingAccessCards(this.targetAppsWithProfiles());
  });

  readonly profileCollapses = signal<Record<string, boolean>>({});

  private readonly shouldLoadWidget = signal(false);
  readonly widgetResource = this.widgetComunicazioniService.createRenderWidgetResource(
    () => this.shouldLoadWidget()
  );
  readonly isLoadingWidgetComunicazioni = this.widgetResource.isLoading;
  readonly widgetComunicazioniHtml = signal<SafeHtml | null>(null);
  readonly widgetComunicazioniCount = signal(0);
  readonly hasWidgetComunicazioniError = computed(() => !!this.widgetResource.error());
  @ViewChild('widgetContent') private widgetContentRef?: ElementRef<HTMLElement>;
  private contactConfirmationModalRef: NgbModalRef | null = null;

  constructor() {
    effect(() => {
      const error = this.targetAppsResource.error();
      if (!error) {
        return;
      }

      Console.error('LandingPage', { message: 'Errore nel caricamento delle target apps con profili', error });
      const apiError = this.apiService.normalizeApiError(error);
      const statusCode = apiError.status_code ?? (apiError as { status?: number }).status;

      if (statusCode === 401) {
        return;
      }
      if (statusCode !== undefined && statusCode >= 500) {
        this.toastService.presentaToastErrore('Errore del server, riprova più tardi');
        return;
      }
      if (statusCode === 0 || error.name === 'TimeoutError' || !navigator.onLine) {
        this.toastService.presentaToastErrore('Errore di connessione, verifica la rete');
        return;
      }
      this.toastService.presentaToastErrore('Errore nel caricamento delle applicazioni disponibili');
    });

    effect(() => {
      if (!this.showWidgetComunicazioni) {
        return;
      }
      if (this.widgetResource.status() !== 'resolved') {
        return;
      }

      const response = this.widgetResource.value();
      const rawHtml = response?.html ?? '';
      const trustedHtml = rawHtml.trim()
        ? this.domSanitizer.bypassSecurityTrustHtml(rawHtml)
        : null;
      this.widgetComunicazioniHtml.set(trustedHtml);
      this.widgetComunicazioniCount.set(response?.count ?? 0);
      setTimeout(() => {
        void this.activateWidgetScripts();
      }, 0);
    });

    effect(() => {
      if (!this.showWidgetComunicazioni) {
        return;
      }
      const error = this.widgetResource.error();
      if (!error) {
        return;
      }
      Console.error('LandingPage', { message: 'Errore nel caricamento widget comunicazioni', error });
      this.widgetComunicazioniHtml.set(null);
      this.widgetComunicazioniCount.set(0);
      this.toastService.presentaToastErrore('Errore nel caricamento del widget comunicazioni');
    });
  }

  ngOnInit(): void {
    // DEBUG: alert URL ricevuto
    if (environment.ENABLE_DEBUG_ALERTS) {
      alert('auth-app : URL ricevuto in LandingPage: ' + window.location.href);
    }

    // Verifica se l'utente è autenticato, altrimenti reindirizza al login
    if (!this.isAuthenticated()) {
      if (environment.ENABLE_DEBUG_ALERTS) {
        alert('auth-app : Redirect a /login (utente non autenticato)');
      }
      this.router.navigateByUrl('/login');
      return;
    }

    // Se c'è un token ma non ci sono dati utente, caricali
    if (this.appStore.token() && !this.user()) {
      this.authService.me().subscribe({
        next: () => {
          // I dati utente vengono salvati automaticamente nello store da authService.me()
          this.initializeLandingData();
        },
        error: (error) => {
          Console.error('LandingPage', { message: 'Errore nel caricamento dati utente', error });
          // Lo stato di autenticazione viene già resettato da authService.me() nel catchError
          // Verifica nuovamente lo stato prima di navigare
          if (!this.isAuthenticated()) {
            this.toastService.presentaToastErrore('Errore nel caricamento dei dati utente');
            if (environment.ENABLE_DEBUG_ALERTS) {
              alert('auth-app : Redirect a /login (errore caricamento dati utente)');
            }
            this.router.navigateByUrl('/login');
          }
        }
      });
    } else {
      // Se i dati utente sono già presenti, carica profili e target apps
      this.initializeLandingData();
    }
  }

  private initializeLandingData(): void {
    this.checkContactVerificationStatus();
    this.shouldLoadTargetApps.set(true);
    if (this.showWidgetComunicazioni) {
      this.shouldLoadWidget.set(true);
    }
  }

  private async activateWidgetScripts(): Promise<void> {
    const host = this.widgetContentRef?.nativeElement;
    if (!host) {
      return;
    }

    // Pulisce eventuali script eseguiti in precedenza per evitare duplicazioni.
    this.document
      .querySelectorAll('script[data-comunicazioni-widget-executed="true"]')
      .forEach((script) => script.remove());

    const scripts = host.querySelectorAll('script');
    for (const originalScript of scripts) {
      const executableScript = this.document.createElement('script');
      executableScript.setAttribute('data-comunicazioni-widget-executed', 'true');
      executableScript.type = originalScript.type || 'text/javascript';

      if (originalScript.src) {
        // Script esterni (es. marked) devono essere completati prima di eseguire i successivi.
        executableScript.async = false;
        executableScript.src = originalScript.src;
        await new Promise<void>((resolve) => {
          executableScript.onload = () => resolve();
          executableScript.onerror = () => resolve();
          this.document.body.appendChild(executableScript);
        });
      } else {
        executableScript.text = originalScript.textContent ?? '';
        this.document.body.appendChild(executableScript);
      }
    }
  }

  private checkContactVerificationStatus(): void {
    const currentUser = this.user();
    if (currentUser) {
      // In landing-page la verifica obbligatoria riguarda solo l'email.
      const shouldShowModal = !currentUser.email_is_verified;
      if (shouldShowModal) {
        this.openContactConfirmationModal();
      }
      return;
    }

    this.verificaService.getStatoVerificaContatti().subscribe({
      next: (stato) => {
        // In landing-page la verifica obbligatoria riguarda solo l'email.
        const shouldShowModal = !stato.email_is_verified;
        if (shouldShowModal) {
          this.openContactConfirmationModal();
        }
      },
      error: (error) => {
        Console.error('LandingPage', { message: 'Errore nel controllo verifica contatti', error });
      }
    });
  }

  onContactConfirmationSent(): void {
    this.router.navigate(['/settings'], {
      queryParams: { openEmailVerificationModal: 'true' }
    });
  }

  private openContactConfirmationModal(): void {
    if (this.contactConfirmationModalRef) {
      return;
    }

    const modalRef = this.modalService.open(ContactConfirmationModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false
    });

    this.contactConfirmationModalRef = modalRef;
    modalRef.componentInstance.title = 'Conferma contatti';
    modalRef.componentInstance.description = this.getContactVerificationDescription();
    modalRef.componentInstance.confirmAction = 'emit';
    modalRef.componentInstance.showCloseButton = false;
    modalRef.componentInstance.showMaybeLater = false;
    void modalRef.result
      .then((result) => {
        if (result === 'confirmed') {
          this.onContactConfirmationSent();
        }
      })
      .catch(() => {
        // Dismiss intenzionale (es. close button o escape se abilitato): nessuna azione.
      })
      .finally(() => {
        this.contactConfirmationModalRef = null;
      });
  }

  getContactVerificationDescription(): string {
    const currentUser = this.user();
    if (!currentUser) {
      return 'Non e stato possibile leggere subito lo stato dei contatti. Premi conferma per proseguire alla verifica nelle impostazioni.';
    }

    const emailStatus = currentUser.email_is_verified ? 'verificata' : 'non verificata';

    return `Stato contatti: email ${emailStatus}. Premi conferma per proseguire alla verifica della mail.`;
  }

  isProfileRoleSameAsApp(appNome: string, ruolo: string | undefined): boolean {
    if (!ruolo) {
      return true;
    }
    return ruolo.toLowerCase().trim() === appNome.toLowerCase().trim();
  }

  hasLandingApps(): boolean {
    return this.raggruppaProfili
      ? this.targetAppsWithProfiles().length > 0
      : this.landingAccessCards().length > 0;
  }

  /**
   * Restituisce il numero totale di opzioni disponibili per una target app
   * (profili associati + eventuale variante senza profilo).
   */
  getTotalOptionsForApp(group: ITargetAppWithProfiles): number {
    return group.profiles.length + (group.noProfileApp ? 1 : 0);
  }

  getProfilesCollapseKey(group: ITargetAppWithProfiles): string {
    return `${group.app.nome}|${group.keys.join(',')}`;
  }

  isProfilesCollapsed(group: ITargetAppWithProfiles): boolean {
    const key = this.getProfilesCollapseKey(group);
    return this.profileCollapses()[key] ?? false;
  }

  toggleProfilesCollapse(group: ITargetAppWithProfiles): void {
    const key = this.getProfilesCollapseKey(group);
    this.profileCollapses.update((state) => ({
      ...state,
      [key]: !(state[key] ?? true)
    }));
  }

  onProfileRequiredCardClick(group: ITargetAppWithProfiles, event: MouseEvent): void {
    if (!group.app.abilitato || group.profiles.length === 0) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    // Evita il warning quando l'utente interagisce già con i controlli interni.
    const clickedInteractiveControl = target.closest('.btn-profile, .profile-collapse-toggle, .app-name-row');
    if (clickedInteractiveControl) {
      return;
    }

    this.toastService.presentaToastWarning('Scegli un profilo!');
  }

  /**
   * Gestisce il click su un profilo di una target app.
   * Genera un ticket SSO e reindirizza all'app con il ticket.
   *
   * @param targetApp Chiave della target app (key)
   * @param idProfilo ID del profilo selezionato
   * @param urlApp URL dell'applicazione di destinazione
   */
  onProfileClick(targetApp: string, idProfilo: number, urlApp: string): void {
    // Trova la target app per verificare se è abilitata
    const targetAppData = this.targetAppsWithProfiles().find((item) =>
      item.keys.includes(targetApp)
    );

    if (!targetAppData || !targetAppData.app.abilitato) {
      this.toastService.presentaToastErrore('Applicazione non abilitata.');
      return;
    }

    // Genera il ticket e redirect usando SsoService (include retry logic con refresh token)
    // Lo spinner segue le regole dell'HTTP interceptor (delay 500ms)
    // Leggi nuova_pagina dalla target app per determinare se aprire in nuova tab
    const openInNewTab = targetAppData.app.nuova_pagina;
    this.ssoService.generateTicketAndRedirectDirect(targetApp, idProfilo, urlApp, {
      fallbackUrl: '/landing-page',
      preserveTargetAppOnError: false,
      openInNewTab: openInNewTab
    }).subscribe({
      error: (error) => {
        // Gli errori sono già gestiti da SsoService (toast e navigazione)
        Console.error('LandingPage', { message: 'Errore nel flusso SSO', error });
      }
    });
  }

  /**
   * Gestisce il click sull'app speciale senza profili.
   * Usa il flusso SSO dedicato che non richiede id_profilo.
   *
   * @param targetApp Chiave della target app (key)
   * @param app Dati dell'applicazione di destinazione
   */
  onAppSenzaProfiloClick(targetApp: string, app: ITargetApp): void {
    if (!app.abilitato) {
      this.toastService.presentaToastErrore('Applicazione non abilitata.');
      return;
    }

    const openInNewTab = app.nuova_pagina;

    this.ssoService.generateTicketNoProfilesAndRedirect(targetApp, app.url, {
      fallbackUrl: '/landing-page',
      preserveTargetAppOnError: false,
      openInNewTab
    }).subscribe({
      error: (error) => {
        Console.error('LandingPage', { message: 'Errore nel flusso SSO senza profilo', error });
      }
    });
  }

  /**
   * Gestisce il click sul pulsante test questionario
   * Naviga alla pagina di test del questionario
   */
  onTestQuestionarioClick(): void {
    if (environment.ENABLE_DEBUG_ALERTS) {
      alert('auth-app : Redirect a /test-questionario');
    }
    this.router.navigateByUrl('/test-questionario');
  }

  onAppIconError(event: Event): void {
    const image = event.target as HTMLImageElement | null;
    if (!image || image.src.endsWith(this.GENERIC_ICON_PATH)) {
      return;
    }

    image.src = this.GENERIC_ICON_PATH;
  }
}
